import { Component } from '@theme/component';
import { cartStore } from '@theme/cart--state--store';
import { eventBus } from '@theme/events';
import { CartActions } from '@theme/cart--state--actions';

const { add, addFailed } = CartActions;

const STORAGE_KEY = 'theme:cart-item-count';

/**
 * @typedef {Object} CartIconRefs
 * @property {HTMLElement} count - The badge element displaying item count
 */

/**
 * Cart Icon Web Component
 *
 * Reactive cart badge that updates via the cart store.
 * Persists item count in sessionStorage for cross-page consistency.
 *
 * Architecture (ngrx-style):
 * - Listens to cart:add (action) for optimistic increment before API completes
 * - Listens to cart:add-failed (result) to rollback optimistic increment
 * - Subscribes to store for authoritative count after API completes
 *
 * @attr {number} data-item-count - Initial cart item count (from server render)
 *
 * @example
 * <cart-icon data-item-count="{{ cart.item_count }}">
 *   <a href="{{ routes.cart_url }}">
 *     <sup ref="count">{{ cart.item_count }}</sup>
 *     {{ 'icon-cart.svg' | inline_asset_content }}
 *   </a>
 * </cart-icon>
 *
 * @extends {Component<CartIconRefs>}
 */
class CartIcon extends Component {
	/** @type {(() => void)[]} */
	#unsubscribes = [];

	/** @type {number} */
	#count = 0;

	connectedCallback() {
		super.connectedCallback();

		// Initialize count: prefer sessionStorage for cross-page, fall back to data attr
		const stored = sessionStorage.getItem(STORAGE_KEY);
		const serverCount = parseInt(this.dataset.itemCount, 10) || 0;
		this.#count = stored !== null ? parseInt(stored, 10) : serverCount;

		// Sync display if stored count differs from server-rendered value
		if (stored !== null && this.#count !== serverCount) {
			this.#render();
		}

		// ─────────────────────────────────────────────────────────────────────────
		// Optimistic UI: respond to action events
		// ─────────────────────────────────────────────────────────────────────────

		// Optimistic increment on cart:add action (before API completes)
		this.#unsubscribes.push(
			eventBus.on(add, (event) => {
				const quantity = event.detail?.data?.quantity ?? 1;
				this.#update(this.#count + quantity);
			})
		);

		// Rollback on failure (store won't update on error)
		this.#unsubscribes.push(
			eventBus.on(addFailed, (event) => {
				// Rollback by the quantity that was attempted
				const quantity = 1; // Default to 1 since we don't have quantity in failure event
				this.#update(Math.max(0, this.#count - quantity));
			})
		);

		// ─────────────────────────────────────────────────────────────────────────
		// Authoritative: subscribe to store for final count
		// ─────────────────────────────────────────────────────────────────────────

		this.#unsubscribes.push(
			cartStore.subscribe(
				(s) => s.itemCount,
				(itemCount) => this.#update(itemCount)
			)
		);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		for (const unsub of this.#unsubscribes) {
			unsub();
		}
		this.#unsubscribes = [];
	}

	/**
	 * Update count and persist.
	 * @param {number} count
	 */
	#update(count) {
		this.#count = count;
		sessionStorage.setItem(STORAGE_KEY, String(count));
		this.#render();
	}

	/**
	 * Re-render the badge to reflect current count.
	 */
	#render() {
		const badge = this.refs.count;
		if (!badge) return;

		if (this.#count > 0) {
			badge.textContent = this.#count;
			badge.hidden = false;
		} else {
			badge.textContent = '';
			badge.hidden = true;
		}
	}
}

if (!customElements.get('cart-icon')) {
	customElements.define('cart-icon', CartIcon);
}
